import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaUser, FaEdit, FaTrash, FaShieldAlt, FaPlus, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { getAllAdmins, getRoles, assignRoleToAdmin, deleteAdmin, toggleAdminStatus, clearState } from '../../../redux/Slices/authSlice';
import { useTitle } from '../../../context/TitleContext';
import { useTranslation } from 'react-i18next';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Link } from 'react-router-dom';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';

const columnHelper = createColumnHelper();

const Admins = () => {
  const { t } = useTranslation('global');
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const { admins, roles, loading, error, success } = useSelector((state) => state.auth);

  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: '', data: null });
  
  // Table states
  const [sorting, setSorting] = useState([]);

  useEffect(() => {
    setTitle('المشرفين');
    dispatch(clearState()); // تنظيف الـ success state القديم
    dispatch(getAllAdmins());
    dispatch(getRoles());
  }, [dispatch, setTitle]);

  useEffect(() => {
    if (success) {
      showSuccessToast(success);
      dispatch(clearState());
      dispatch(getAllAdmins()); // Refresh data
      setShowAssignModal(false);
      setSelectedAdmin(null);
      setSelectedRole('');
    }
    if (error) {
      showErrorToast(error);
      dispatch(clearState());
    }
  }, [success, error, dispatch]);

  const handleAssignRole = (admin) => {
    setSelectedAdmin(admin);
    setShowAssignModal(true);
  };

  const handleSubmitAssignRole = () => {
    if (!selectedRole) {
      showErrorToast('يرجى اختيار دور');
      return;
    }

    dispatch(assignRoleToAdmin({
      adminId: selectedAdmin.id,
      roleId: selectedRole
    }));
  };

  const handleDeleteAdmin = (adminId, adminName) => {
    setConfirmModal({
      isOpen: true,
      type: 'delete',
      data: { adminId, adminName },
      message: `هل أنت متأكد من حذف المشرف "${adminName}"؟`,
      title: 'تأكيد الحذف'
    });
  };

  const executeDeleteAdmin = () => {
    dispatch(deleteAdmin(confirmModal.data.adminId));
  };

  const handleToggleStatus = (adminId) => {
    dispatch(toggleAdminStatus(adminId));
  };

  const handleConfirmAction = () => {
    if (confirmModal.type === 'delete') {
      executeDeleteAdmin();
    }
  };

  // التحقق من كون المشرف Super Admin
  const isSuperAdmin = (adminId) => {
    return adminId === 1; // Super Admin له id = 1
  };

  // تعريف الأعمدة
  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        id: 'id',
        header: '#',
        cell: ({ row }) => (
          <div className="order-number-badge">
            #{row.index + 1}
          </div>
        ),
        size: 60,
        minSize: 60,
        maxSize: 60,
      }),
      columnHelper.display({
        id: 'image',
        header: 'الصورة',
        cell: ({ row }) => {
          const admin = row.original;
          return (
            <div className="admin-avatar-container">
              {admin.image ? (
                <img
                  src={admin.image}
                  alt={admin.name || 'مشرف'}
                  className="admin-avatar"
                />
              ) : (
                <div className="default-placeholder">
                  <FaUser />
                </div>
              )}
            </div>
          );
        },
        size: 80,
        minSize: 70,
        maxSize: 90,
      }),
      columnHelper.accessor('name', {
        id: 'name',
        header: 'اسم المشرف',
        cell: ({ row }) => (
          <div className="admin-name">
            {row.original.name || 'بدون اسم'}
          </div>
        ),
        size: 150,
        minSize: 120,
        maxSize: 180,
      }),
      columnHelper.accessor('email', {
        id: 'email',
        header: 'البريد الإلكتروني',
        cell: ({ row }) => (
          <div className="email-display">
            {row.original.email}
          </div>
        ),
        size: 200,
        minSize: 180,
        maxSize: 250,
      }),
      columnHelper.accessor('phone', {
        id: 'phone',
        header: 'الهاتف',
        cell: ({ row }) => (
          <div className="phone-badge">
            {row.original.phone || 'غير محدد'}
          </div>
        ),
        size: 120,
        minSize: 100,
        maxSize: 140,
      }),
      columnHelper.accessor('status', {
        id: 'status',
        header: 'الحالة',
        cell: ({ row }) => (
          <span className={`badge ${row.original.status === '1' ? 'bg-success' : 'bg-secondary'} text-white`}>
            {row.original.status === '1' ? 'نشط' : 'غير نشط'}
          </span>
        ),
        size: 100,
        minSize: 80,
        maxSize: 120,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'الإجراءات',
        cell: ({ row }) => {
          const admin = row.original;
          return (
            <div className="action-buttons">
              {!isSuperAdmin(admin.id) && (
                <>
                  <Link to={`/edit-admin/${admin.id}`} onClick={() => {
                    sessionStorage.setItem(`admin_${admin.id}`, JSON.stringify({
                      id: admin.id,
                      name: admin.name,
                      email: admin.email,
                      phone: admin.phone,
                      image: admin.image
                    }));
                  }}>
                    <motion.button
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      className="btn btn-edit"
                      title="تعديل">
                      <FaEdit />
                    </motion.button>
                  </Link>
                  <motion.button
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn btn-role"
                    onClick={() => handleAssignRole(admin)}
                    title="تعيين دور">
                    <FaShieldAlt />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn btn-toggle btn-sm"
                    onClick={() => handleToggleStatus(admin.id)}
                    title={admin.status === '1' ? 'إلغاء تفعيل' : 'تفعيل'}>
                    {admin.status === '1' ? <FaToggleOff className="mx-1" /> : <FaToggleOn className="mx-1" />}
                    {admin.status === '1' ? 'إلغاء تفعيل' : 'تفعيل'}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn btn-delete"
                    onClick={() => handleDeleteAdmin(admin.id, admin.name)}
                    title="حذف">
                    <FaTrash />
                  </motion.button>
                </>
              )}
              {isSuperAdmin(admin.id) && (
                <span className="badge bg-info text-white px-3 py-2">
                  Super Admin
                </span>
              )}
            </div>
          );
        },
        size: 400,
        minSize: 350,
        maxSize: 450,
      }),
    ],
    [roles]
  );

  // إعداد الجدول
  const table = useReactTable({
    data: admins || [],
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  const LoadingSpinner = () => (
    <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  return (
    <div className="main_content">
      <div className="dashboard-container">
        
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="d-flex justify-content-between align-items-center mb-4"
        >
          <h4 className="fw-bold text-dark">قائمة المشرفين</h4>
          <Link to="/add-admin">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn btn-primary"
              style={{ 
                borderRadius: '10px',
                backgroundColor: 'rgb(24, 65, 148)',
                borderColor: 'rgb(24, 65, 148)'
              }}
            >
              <FaPlus className="mx-2" />
              إضافة مشرف
            </motion.button>
          </Link>
        </motion.div>


        {/* Admins Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="card shadow-sm"
        >
          <div className="card-body p-0">
            {loading ? (
              <LoadingSpinner />
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-modern">
                    <thead>
                      {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <th
                              key={header.id}
                              className="text-center"
                              style={{ width: header.getSize() }}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map((row, index) => (
                          <motion.tr
                            key={row.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            className="table-row-modern"
                          >
                            {row.getVisibleCells().map(cell => (
                              <td key={cell.id} className="text-center">
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            ))}
                          </motion.tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={columns.length} className="text-center py-4">
                            <div className="text-muted">
                              <FaUser className="mb-2" style={{ fontSize: '2rem' }} />
                              <p className="mb-0">لا توجد مشرفين متاحين</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {table.getPageCount() > 1 && (
                  <div className="card-footer bg-light">
                    <div className="row align-items-center">
                      <div className="col-md-6">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span className="text-muted">الانتقال إلى الصفحة</span>
                          <div className="input-group" style={{ width: 'auto' }}>
                            <span className="input-group-text" style={{ fontSize: '12px', color: '#6c757d', fontWeight: 'bold' }}>
                              #
                            </span>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={table.getState().pagination.pageIndex + 1}
                              onChange={(e) => {
                                const value = parseInt(e.target.value);
                                if (!isNaN(value) && value > 0) {
                                  const page = value - 1;
                                  table.setPageIndex(page);
                                }
                              }}
                              min="1"
                              max={table.getPageCount()}
                              style={{ width: '60px' }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="d-flex justify-content-end gap-2 flex-wrap">
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => table.setPageIndex(0)}
                            disabled={!table.getCanPreviousPage()}
                          >
                            الأولى
                          </button>
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                          >
                            السابقة
                          </button>
                          <span className="d-flex align-items-center px-3">
                            صفحة {table.getState().pagination.pageIndex + 1} من {table.getPageCount()}
                          </span>
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                          >
                            التالية
                          </button>
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                            disabled={!table.getCanNextPage()}
                          >
                            الأخيرة
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>

        {/* Assign Role Modal */}
        {showAssignModal && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title mx-3">تعيين دور للمشرف</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowAssignModal(false);
                      setSelectedAdmin(null);
                      setSelectedRole('');
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  {selectedAdmin && (
                    <div className="mb-3">
                      <label className="form-label fw-semibold">المشرف:</label>
                      <div className="d-flex align-items-center">
                        <div className="avatar-sm bg-primary-subtle rounded-circle d-flex align-items-center justify-content-center mx-3">
                          <FaUser className="text-primary" />
                        </div>
                        <div>
                          <h6 className="mb-0">{selectedAdmin.name}</h6>
                          <small className="text-muted">{selectedAdmin.email}</small>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label className="form-label fw-semibold">اختر الدور:</label>
                    <select
                      className="form-select"
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                    >
                      <option value="">اختر دور...</option>
                      {roles && roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name || `الدور ${role.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{
                      backgroundColor: 'rgb(24, 65, 148)',
                      borderColor: 'rgb(24, 65, 148)',
                      color: 'white'
                    }}
                    onClick={() => {
                      setShowAssignModal(false);
                      setSelectedAdmin(null);
                      setSelectedRole('');
                    }}
                  >
                    إلغاء
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{
                      backgroundColor: 'rgb(24, 65, 148)',
                      borderColor: 'rgb(24, 65, 148)'
                    }}
                    onClick={handleSubmitAssignRole}
                    disabled={!selectedRole}
                  >
                    تعيين الدور
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal({ isOpen: false, type: '', data: null })}
          onConfirm={handleConfirmAction}
          title={confirmModal.title || 'تأكيد العملية'}
          message={confirmModal.message || 'هل أنت متأكد من هذا الإجراء؟'}
          confirmText="حذف"
          cancelText="إلغاء"
          confirmButtonColor="#dc3545"
          type="danger"
        />
      </div>
    </div>
  );
};

export default Admins;

// CSS للتصميم الحديث
const style = document.createElement('style');
style.textContent = `
  /* تصميم الجدول الحديث */
  .table-modern {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border: none;
  }
  
  .table-modern thead {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  }
  
  .table-modern thead th {
    border: none;
    padding: 1rem;
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .table-modern tbody tr {
    border-bottom: 1px solid #f1f5f9;
    transition: all 0.3s ease;
  }
  
  .table-modern tbody tr:hover {
    background-color: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  .table-modern tbody td {
    border: none;
    padding: 1rem;
    vertical-align: middle;
  }
  
  /* تصميم رقم المشرف */
  .order-number-badge {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-weight: 600;
    font-size: 0.875rem;
    display: inline-block;
    min-width: 60px;
    text-align: center;
  }
  
  /* تصميم معلومات المشرف */
  .admin-info {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
    justify-content: center;
  }
  
  .admin-avatar-container {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  .admin-avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    object-fit: cover;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  /* أيقونات افتراضية للبيانات الفارغة */
  .default-placeholder {
    width: 50px;
    height: 50px;
    background: #f8fafc;
    border: 2px dashed #cbd5e1;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    font-size: 1.2rem;
    transition: all 0.3s ease;
  }
  
  .default-placeholder:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
    color: #64748b;
  }
  
  /* تصميم اسم المشرف */
  .admin-name {
    font-weight: 500;
    color: #374151;
    font-size: 0.875rem;
  }
  
  /* تصميم البريد الإلكتروني */
  .email-display {
    background: #f8fafc;
    color: #64748b;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    border: 1px solid #e2e8f0;
    max-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  /* تصميم رقم الهاتف */
  .phone-badge {
    background: #f0f9ff;
    color: #0369a1;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.875rem;
    border: 1px solid #bae6fd;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  /* تصميم أزرار الإجراءات */
  .action-buttons {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    flex-direction: row;
    flex-wrap: nowrap;
  }
  
  .btn-edit, .btn-role, .btn-delete {
    border: none;
    color: white !important;
    padding: 0.15rem !important;
    border-radius: 8px;
    font-size: 0.8rem !important;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    width: 28px !important;
    height: 28px !important;
    min-width: 28px !important;
    min-height: 28px !important;
  }
  
  .btn-delete {
    font-size: 0.75rem !important;
  }
  
  .btn-edit {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
  }
  
  .btn-role {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
  }
  
  .btn-toggle {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
    box-shadow: 0 2px 4px rgba(245, 158, 11, 0.3);
    min-width: 100px !important;
    max-width: 120px !important;
    width: auto !important;
    font-size: 12px !important;
    text-align: center !important;
    white-space: nowrap !important;
    overflow: visible !important;
    text-overflow: unset !important;
    padding: 6px 8px !important;
    font-weight: 500 !important;
    color: white !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  
  .btn-toggle * {
    color: white !important;
    visibility: visible !important;
    opacity: 1 !important;
  }
  
  .btn-delete {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    box-shadow: 0 2px 4px rgba(239, 68, 68, 0.3);
  }
  
  .btn-edit:hover, .btn-role:hover, .btn-toggle:hover, .btn-delete:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  .btn-edit * {
    color: white !important;
  }
  
  .btn-edit svg,
  .btn-role svg,
  .btn-delete svg {
    width: 12px !important;
    height: 12px !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  
  /* تحسين البادج */
  .badge {
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
`;
document.head.appendChild(style);
